import type {IntervalString} from 'shared/ui-widgets/daterange-picker';
import type {Directory, DirectoryNode, PayloadActionResponse, Rec, RecordHeader, RecordSearchParams, ReportTemplate, UUID} from 'src/typings';
import type {DirectoryDataService} from 'shared/data-services';
import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {ModalDialogService} from 'shared/modal-dialogs';
import type {FinishedResponseInteractionService} from 'shared/services';
import {DetailLevel, Kind, SearchType, ViewFormat} from 'shared/constants';
import {FilterOperatorEnum, FilterableDatatype, createFilterQtString, mapFieldTypeIdToFilterField} from 'src/features/search/kp-search-field-type-filter';
import type {GridDeleteRequest, GridEditRequest, RecordRow} from 'src/features/record-grid/lib/types';
import type {LogbookPairingBatchExportModalModel, LogbookPairingExportSingularPayload} from './types';
import type {RecordGridDataService} from 'src/features/record-grid/services/record-grid.data-service';
import type {FileService} from 'src/features/file/file.service';
import type {SearchService} from 'src/features/search/search.service';
import type ReportServerDataService from 'src/features/report-server/services/report-server.data-service';
import {convertFieldTypeIdToFieldId} from 'shared/utils/record-field-utils';
import {exists} from 'shared/utils/custom-utils';
import {getDateFromGridFieldValue, getTextFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
import {FOND_INSTALLATION_LOGBOOK, FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
import {SUTOR_LOGBOOK_PAIRING_PAGE} from 'src/features/erp-sutor/sutor-constants';
import LogbookPairingBatchExportModal from './LogbookPairingBatchExportModal.svelte';
import LogbookPairingExportSingularModal from './LogbookPairingExportSingularModal.svelte';

export class ProjectLogbookPairingService {
    public static serviceName = 'projectLogbookPairingService';

    private static logbookExportsFolderName = 'Exporty montážních deníků';

    /*@ngInject*/
    constructor(private recordGridDataService: RecordGridDataService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private reportServerDataService: ReportServerDataService,
                private directoryDataService: DirectoryDataService,
                private fileService: FileService,
                private toastMessageService: ToastMessageService,
                private modalDialogService: ModalDialogService,
                private searchService: SearchService) {
    }

    public async pairReportWithInstallationLogbook(reportRecord: RecordRow, installationLogbookId: UUID | null): Promise<RecordRow> {
        try {
            if (exists(installationLogbookId)) {
                await this.recordGridDataService.editCells(this.createInstallationLogbookFieldEditRequest(reportRecord.id, installationLogbookId));
            } else {
                await this.recordGridDataService.deleteCells(this.createInstallationLogbookFieldDeleteRequest(reportRecord.id));
            }

            return await this.recordGridDataService.loadSingleRow(reportRecord.id);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }

    private createInstallationLogbookFieldEditRequest(editedRecordId: UUID, installationLogbookId: UUID | null): GridEditRequest {
        return {
            cellEditRequests: [
                {
                    record: editedRecordId,
                    fieldId: convertFieldTypeIdToFieldId(FOND_REPORT.installationLogbookFieldId, [0, 0]),
                    setFieldValueRequest: {recordId: installationLogbookId},
                    additionalInvalidatingRecords: []
                }
            ]
        };
    }

    private createInstallationLogbookFieldDeleteRequest(editedRecordId: UUID): GridDeleteRequest {
        return {
            cellDeleteRequests: [
                {
                    record: editedRecordId,
                    fieldId: convertFieldTypeIdToFieldId(FOND_REPORT.installationLogbookFieldId, [0, 0])
                }
            ]
        };
    }

    public async getReportServerTemplateByName(name: string): Promise<ReportTemplate | null> {
        try {
            return await this.reportServerDataService.getReportTemplateByName(name);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            return null;
        }
    }

    public async saveLogbookExportToProjectFiles(logbookRecord: RecordRow, projectRecord: Rec, file: File): Promise<void> {
        try {
            const directory = await this.getOrCreateLogbooksDirectory(projectRecord);
            if (!exists(directory)) {
                return;
            }

            const result = await this.modalDialogService.openModalWindowFromComponent<void, PayloadActionResponse<LogbookPairingExportSingularPayload>>(LogbookPairingExportSingularModal, null);
            await this.uploadExportedLogbookToProjectFiles(logbookRecord, file, directory, result.payload.divideExportsByDate, result.payload.replaceExisting);

            this.toastMessageService.showSuccess(`Export byl úspěšně uložen do souborů projektu do složky ${ProjectLogbookPairingService.logbookExportsFolderName}!`);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
        }
    }

    public async openBatchExportLogbooksToProjectFilesModal(projectRecord: Rec): Promise<void> {
        try {
            await this.modalDialogService.openModalWindowFromComponent<LogbookPairingBatchExportModalModel>(LogbookPairingBatchExportModal, {projectRecord});
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
        }
    }

    public async batchExportLogbooksToProjectFiles(projectRecord: Rec, interval: IntervalString, divideExportsByDate: boolean, replaceExisting: boolean, startCallback: (totalLogbooks: number) => void, progressCallback: (done: number, total: number) => void): Promise<void> {
        try {
            const logbookReportTemplate = await this.getReportServerTemplateByName(SUTOR_LOGBOOK_PAIRING_PAGE.reportServerTemplateMD);
            const directory = await this.getOrCreateLogbooksDirectory(projectRecord);
            if (!exists(logbookReportTemplate) || !exists(directory)) {
                return;
            }

            const result = await this.searchService.search<RecordHeader, RecordSearchParams>({
                kind: [Kind.KIND_RECORD],
                type: SearchType.TYPE_GLOBAL,
                viewFormat: ViewFormat.TABLE_ROW,
                rootFond: [{id: FOND_INSTALLATION_LOGBOOK.fond}],
                fond: [{id: FOND_INSTALLATION_LOGBOOK.fond}],
                detailLevel: DetailLevel.HEADER,
                pageSize: 5000,
                qt: createFilterQtString({
                    filteredValues: {
                        [mapFieldTypeIdToFilterField(FOND_INSTALLATION_LOGBOOK.parentItemFieldId, 'origin')]: {
                            datatype: FilterableDatatype.RECORD,
                            val: projectRecord.id
                        },
                        [mapFieldTypeIdToFilterField(FOND_INSTALLATION_LOGBOOK.dateFieldId, 'val')]: {
                            datatype: FilterableDatatype.DATE,
                            filterOperator: FilterOperatorEnum.within,
                            val: interval
                        }
                    }
                })
            });

            startCallback(result.result.totalElements);

            const logbookRecords = result.result.content;

            for (let index = 0; index < logbookRecords.length; index++) {
                const logbookRecordHeader = logbookRecords[index];
                const logbookRecord = await this.recordGridDataService.loadSingleRow(logbookRecordHeader.id);
                const fileName = this.getLogbookExportFileName(logbookRecord);
                const exportedFile = await this.reportServerDataService.getBlobExportFile(logbookReportTemplate, {recordId: logbookRecord.id}, fileName);

                await this.uploadExportedLogbookToProjectFiles(logbookRecord, exportedFile, directory, divideExportsByDate, replaceExisting);

                progressCallback(index + 1, logbookRecords.length);
            }

            this.toastMessageService.showSuccess(`Exporty byly úspěšně uloženy do souborů projektu do složky ${ProjectLogbookPairingService.logbookExportsFolderName}!`);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
        }
    }

    public getLogbookExportFileName(logbookRecord: RecordRow): string {
        const parentItemField = getTextFromGridFieldValue(getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.parentItemFieldId), null);
        const customerProjectField = getTextFromGridFieldValue(getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.customerProjectFieldId), null);
        const dateField = getTextFromGridFieldValue(getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.dateFieldId), null);
        const name = logbookRecord.name;

        const joinedName = [parentItemField, customerProjectField, dateField, name].filter((v) => exists(v)).join('-');
        return `${joinedName}.pdf`;
    }

    private async getOrCreateLogbooksDirectory(projectRecord: Rec): Promise<Directory | null> {
        const directoryTree = await this.directoryDataService.getDirectoryTree(projectRecord.directoryId);
        const foundDirectory = directoryTree.children.find((child) => child.name === ProjectLogbookPairingService.logbookExportsFolderName);

        let directory: DirectoryNode | Directory = foundDirectory;

        if (!exists(foundDirectory)) {
            const result = await this.directoryDataService.createSubdirectory(projectRecord.directoryId, ProjectLogbookPairingService.logbookExportsFolderName);
            directory = result.savedObject;
        }

        if (!exists(directory)) {
            this.toastMessageService.showError('Nastala chyba při vytváření složky pro exporty montážních deníků!');
            return null;
        }

        return directory;
    }

    private async uploadExportedLogbookToProjectFiles(logbookRecord: RecordRow, exportedFile: File, rootDir: Directory, divideExportsByDate: boolean, replaceExisting: boolean): Promise<void> {
        const date = getDateFromGridFieldValue(getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.dateFieldId));
        let directory = rootDir;
        if (divideExportsByDate && exists(date)) {
            const dirName = date.toFormat('yyyy-MM-dd');
            directory = await this.getOrCreateSubdirectory(rootDir, dirName);
        }

        const existingFiles = await this.fileService.getFilesByDirectory(directory.id);

        if (!replaceExisting) {
            const fileVersion = existingFiles.filter((file) => file.name.includes(exportedFile.name)).length + 1;

            if (fileVersion === 1) {
                await this.fileService.uploadFileToDirectory(directory.id, exportedFile);
                return;
            }

            const newFileName = `(${fileVersion})-${exportedFile.name}`;
            const renamedFile = new File([exportedFile], newFileName, {
                type: exportedFile.type,
                lastModified: exportedFile.lastModified
            });

            await this.fileService.uploadFileToDirectory(directory.id, renamedFile);

            return;
        }

        const filesToDelete = existingFiles.filter((file) => file.name === exportedFile.name);

        for (const file of filesToDelete) {
            await this.fileService.deleteFile(file);
        }

        await this.fileService.uploadFileToDirectory(directory.id, exportedFile);
    }

    private async getOrCreateSubdirectory(parentDirectory: Directory, name: string): Promise<Directory> {
        const parentTree = await this.directoryDataService.getDirectoryTree(parentDirectory.id);
        const foundDirectory = parentTree.children.find((child) => child.name === name);

        if (exists(foundDirectory)) {
            return foundDirectory;
        }

        const result = await this.directoryDataService.createSubdirectory(parentDirectory.id, name);
        return result.savedObject;
    }
}
